import { useState } from 'react';

import { currentTheme } from '@/shared/lib/theme';
import { useTranslation } from 'react-i18next';

import {
  type Background,
  columnBackdrops,
  type Look,
  NEUTRAL_COLUMN,
  NEUTRAL_SURFACE,
} from '@/entities/appearance';
import {
  type Column,
  columnAppearanceStyles,
  columnProps,
  useColumnBackgroundImage,
  useSetColumnAppearance,
  useSetColumnCardLook,
} from '@/entities/board';
import {
  BackgroundEditor,
  isLookValid,
  LookEditor,
  LookPreview,
  lookPayload,
  TextColors,
  useBackgroundImageDraft,
} from '@/features/appearance-editor';
import { FormError } from '@/shared/forms';
import { Button, Dialog } from '@/shared/ui';

import {
  COLUMN_STYLES,
  type ColumnTextColors,
  NO_TEXT_COLORS,
  columnInput,
  lookOfColumn,
  textOfColumn,
} from './column-look';
import kanban from './Kanban.module.css';
import styles from './ColumnAppearanceDialog.module.css';

interface Props {
  column: Column;
  boardId: string;
  onClose: () => void;
}

type Tab = 'look' | 'background' | 'cards';
const TABS: readonly Tab[] = ['look', 'background', 'cards'];
const CARD_STYLES = ['header', 'border', 'accent', 'soft', 'solid', 'gradient', 'pattern'] as const;

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Column look in three steps: the column itself (style + color), its background, and the
 * default look of its cards. A live preview stays on top; nothing reaches the server until Save.
 */
export function ColumnAppearanceDialog({ column, boardId, onClose }: Props) {
  const { t } = useTranslation();
  const setAppearance = useSetColumnAppearance(boardId);
  const setCardLook = useSetColumnCardLook(boardId);
  const backgroundImage = useColumnBackgroundImage(boardId);
  const initialLook = lookOfColumn(column);
  const initialBackground = column.appearance?.background ?? null;
  const [tab, setTab] = useState<Tab>('look');
  const [look, setLook] = useState<Look | null>(initialLook);
  const [background, setBackground] = useState<Background | null>(initialBackground);
  const initialText = textOfColumn(column);
  const [text, setText] = useState<ColumnTextColors>(initialText);
  const [cardLook, setCardLookDraft] = useState<Look | null>(column.card_look);
  const image = useBackgroundImageDraft(column.background_image_url);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const hasImage = image.previewUrl !== null;
  const effectiveBackground = background?.kind === 'image' && !hasImage ? null : background;
  const valid = isLookValid(look) && isLookValid(cardLook);
  const input = columnInput(look, effectiveBackground, text);
  const theme = currentTheme() === 'dark' ? 'dark' : 'light';
  const columnSamples = columnBackdrops(look, NEUTRAL_COLUMN[theme]);
  const descriptionBackdrop = [text.description_surface ?? NEUTRAL_SURFACE[theme]];
  const preview = columnProps({
    color: input.color,
    appearance_mode: input.appearance_mode,
    appearance: input.extra,
    background_image_url: image.previewUrl,
  });

  const save = async () => {
    setPending(true);
    setError(null);
    try {
      if (image.file) {
        await backgroundImage.upload.mutateAsync({ columnId: column.id, file: image.file });
      } else if (image.removed && column.background_image_url) {
        await backgroundImage.remove.mutateAsync(column.id);
      }
      const lookChanged = !same(input, columnInput(initialLook, initialBackground, initialText));
      if (lookChanged || image.file || image.removed) {
        await setAppearance.mutateAsync({ columnId: column.id, ...input });
      }
      if (!same(lookPayload(cardLook), column.card_look)) {
        await setCardLook.mutateAsync({ columnId: column.id, look: lookPayload(cardLook) });
      }
      onClose();
    } catch (reason) {
      setError(reason);
      setPending(false);
    }
  };

  const reset = () => {
    setText({ ...NO_TEXT_COLORS });
    setLook(null);
    setBackground(null);
    image.remove();
  };

  return (
    <Dialog title={t('columns.appearanceTitle', { name: column.name })} onClose={onClose} size="lg">
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <div className={styles.previewRow}>
          <div
            className={`${styles.preview} ${columnAppearanceStyles.frame}`}
            data-testid="column-preview"
            aria-label={t('columns.preview')}
            role="img"
            {...preview}
          >
            <div
              className={`${styles.previewHead} ${columnAppearanceStyles.head} ${columnAppearanceStyles.chrome}`}
            >
              {column.name}
            </div>
            <div className={kanban.description} data-testid="description-preview">
              {t('columns.previewDescription')}
            </div>
            <LookPreview look={lookPayload(cardLook)} label={t('columns.previewCards')} />
            <div className={kanban.inlineForm} data-testid="form-preview">
              <input
                className={kanban.inlineInput}
                readOnly
                tabIndex={-1}
                aria-label={t('cards.title')}
                placeholder={t('cards.title')}
              />
            </div>
          </div>
          <p className={styles.previewHint}>{t('columns.appearanceHint')}</p>
        </div>

        <div className={styles.tabs} role="tablist" aria-label={t('columns.appearanceSections')}>
          {TABS.map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              className={`${styles.tab} ${tab === value ? styles.tabOn : ''}`}
              aria-selected={tab === value}
              onClick={() => {
                setTab(value);
              }}
            >
              {t(`columns.sections.${value}`)}
            </button>
          ))}
        </div>

        {tab === 'look' && (
          <>
            <LookEditor value={look} onChange={setLook} allowed={COLUMN_STYLES} idPrefix="column" />
            <TextColors
              idPrefix="column"
              fields={[
                {
                  key: 'title_color',
                  label: t('appearance.text.fields.columnTitle'),
                  role: 'text',
                },
                { key: 'text_color', label: t('appearance.text.fields.columnText'), role: 'text' },
                {
                  key: 'description_color',
                  label: t('appearance.text.fields.descriptionColor'),
                  role: 'text',
                },
                {
                  key: 'form_background',
                  label: t('appearance.text.fields.formBackground'),
                  role: 'surface',
                },
                {
                  key: 'form_text',
                  label: t('appearance.text.fields.formText'),
                  role: 'text',
                },
                {
                  key: 'form_border',
                  label: t('appearance.text.fields.formBorder'),
                  role: 'surface',
                },
                {
                  key: 'description_surface',
                  label: t('appearance.text.fields.descriptionSurface'),
                  role: 'surface',
                },
              ]}
              values={text}
              onChange={(key, value) => {
                setText((current) => ({ ...current, [key]: value }));
              }}
              backdrops={(key) =>
                key === 'description_color'
                  ? descriptionBackdrop
                  : key === 'form_text'
                    ? [text.form_background ?? '#ffffff']
                    : columnSamples
              }
            />
          </>
        )}
        {tab === 'background' && (
          <BackgroundEditor
            value={background}
            onChange={setBackground}
            image={image}
            kinds={['none', 'image']}
            idPrefix="column"
          />
        )}
        {tab === 'cards' && (
          <div className={styles.cards}>
            <p className={styles.hint}>{t('columns.cardLookHint')}</p>
            <LookEditor
              value={cardLook}
              onChange={setCardLookDraft}
              allowed={CARD_STYLES}
              idPrefix="card-look"
            />
            {cardLook && (
              <Button
                variant="ghost"
                onClick={() => {
                  setCardLookDraft(null);
                }}
              >
                {t('columns.resetCardLook')}
              </Button>
            )}
          </div>
        )}

        <FormError error={error} />
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={pending || !valid}>
            {t('common.save')}
          </Button>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <span className={styles.spacer} />
          <Button variant="ghost" onClick={reset}>
            {t('columns.resetAppearance')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
